//@ts-strict-ignore
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DetailsHeader,
  DetailsList,
  DetailsListLayoutMode,
  IDetailsHeaderBaseProps,
  IDetailsListProps,
  Selection,
  SelectionMode,
} from '@fluentui/react/lib/DetailsList';
import { ShimmeredDetailsList } from '@fluentui/react/lib/ShimmeredDetailsList';
import { ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { Sticky, StickyPositionType } from '@fluentui/react/lib/Sticky';
import getIn from '../connectedForm/utils/getIn';
import { initializeIcons } from '@fluentui/react/lib/Icons';
import { TextField } from '@fluentui/react/lib/TextField';
import { MarqueeSelection } from '@fluentui/react/lib/MarqueeSelection';
import { IColumn } from '@fluentui/react/src/components/DetailsList/DetailsList.types';
import { ISelection } from '@fluentui/utilities/lib/selection/Selection.types';
import { IDetailsRowProps } from '@fluentui/react/src/components/DetailsList/DetailsRow.types';
import { FilterOperation, IFilterInput } from '../../graphql/schema/p4types';
import { HoverCard, HoverCardType } from '@fluentui/react';
import { IDropdownOptionRenderer, IRemoteFilterGetter } from '../../@types/entityTypes';

initializeIcons('/fonts/');

const onRenderPlainCard = ({ value }: any): JSX.Element => {
  return <span style={{ padding: '5px' }}>{value}</span>;
};

const onRenderItemColumn: (item?: any, index?: number, column?: IColumn) => React.ReactNode = (item, index, column) => {
  if (!column) return null;
  const data: undefined | { optionRenderer: IDropdownOptionRenderer } = column.data;
  const fieldContent = getIn(item, column.fieldName);

  if (data?.optionRenderer) {
    const Option = data.optionRenderer;
    return <Option option={item} />;
  }

  if (fieldContent && fieldContent?.length > 20) {
    return (
      <HoverCard
        plainCardProps={{
          onRenderPlainCard: onRenderPlainCard,
          renderData: { value: fieldContent },
        }}
        instantOpenOnClick
        type={HoverCardType.plain}
      >
        <span>{fieldContent}</span>
      </HoverCard>
    );
  }
  return <span>{fieldContent}</span>;
};

const getKeyFromEntity = (entity: any) => entity.uuid || entity.id || entity.key;

/**
 * Generate a helper function to check if the selection list (As an array of uuid's) has changed since the function was last called.
 * (Used to close a render loop that can occur due to
 *   filters changing the list, which causes the detailsList to consider the selection changed, so it submits new filters, etc).
 */
const useSelectionComparison = (): ((newSelectedUuids: Array<string>) => boolean) => {
  const selectedUuids = useRef<Array<string>>([]);
  return (newSelectedUuids) => {
    const prevSelectedUuids = selectedUuids.current;
    selectedUuids.current = newSelectedUuids;

    return (
      newSelectedUuids.length !== prevSelectedUuids.length ||
      !newSelectedUuids.every((uuid) => prevSelectedUuids.some((prevUuid) => uuid === prevUuid))
    );
  };
};

export const basicRemoteFilterGetter: IRemoteFilterGetter = ({ fieldName, fieldValue }) => [
  {
    filterOperation: FilterOperation.Contains,
    filterParameters: {
      field: fieldName,
      value: fieldValue,
    },
  },
];

export const genericRemoteFilterGetter: IRemoteFilterGetter = ({ fieldValue }) => [
  {
    filterOperation: FilterOperation.Contains,
    filterParameters: {
      field: 'generic',
      value: fieldValue,
    },
  },
];

const FluentList = ({
  columns,
  items,
  selectedItems = [],
  onItemsSelected,
  enableRowSelect,
  infinite = false,
  queueRequestFor = undefined,
  totalRows = 0,
  enableLocalSearch = false,
  enableRemoteSearch,
  setFilters: setRemoteFilters,
  remoteFilterFieldName,
  remoteFilterGetter = basicRemoteFilterGetter,
  isHeaderVisible = true,
}: {
  columns: Array<IColumn>;
  items: Array<any>;
  selectedItems?: Array<any>;
  onItemsSelected: (items: Array<any>, selectedAll: boolean) => void;
  enableRowSelect: boolean;
  infinite?: boolean;
  queueRequestFor?: (rowIndex: number) => void;
  totalRows?: number;
  enableLocalSearch?: boolean;
  enableRemoteSearch?: boolean;
  setFilters?: (filters: Array<IFilterInput>) => void;
  remoteFilterFieldName?: string; //Special usage for luckins enquiry filters.
  remoteFilterGetter?: IRemoteFilterGetter;
  isHeaderVisible?: boolean;
}) => {
  //Search Field
  const [filter, setFilter] = useState(null);
  const selection = useRef<ISelection<any>>();
  const [, setSelectionState] = useState<any>(); //Just need a state setter to force a render after mount.

  const filteredItems = useMemo(() => {
    return items && filter && enableLocalSearch
      ? items.filter((item) => item[columns[0]!.fieldName!]?.toLowerCase?.().includes(filter))
      : items;
  }, [columns, enableLocalSearch, filter, items]);
  //Include an extra mock item for infinite scrolling lists - Variable not used for a non-infinite configured list.
  const infiniteItems = useMemo(
    () => (items.length !== totalRows ? [...filteredItems, null] : filteredItems),
    [filteredItems, items.length, totalRows]
  );

  const selectionComparison = useSelectionComparison();

  useEffect(() => {
    selection.current = new Selection({
      onSelectionChanged: () => {
        const newSelectedItems = selection.current!.getSelection();
        const isAllSelected = !!selection.current?.isAllSelected();
        const newSelectedUuids = newSelectedItems.map((value) => value.uuid);

        //Comparison check here is to close a render loop that can occur.
        if (selectionComparison(newSelectedUuids)) {
          onItemsSelected?.(newSelectedItems, isAllSelected);
        }
      },
      getKey: getKeyFromEntity,
      //Doing this to prevent 'select-all->deselect' behaviour because it will not work with FluentList selections, and better to prevent it than 'allow it but it's broken'.
      canSelectItem: () => !selection.current?.isAllSelected(),
    });

    selection.current!.setItems(filteredItems, true);
    selectedItems?.forEach((selectedItem) => {
      if (!selectedItem) return;
      selection.current!.setKeySelected(`${getKeyFromEntity(selectedItem)}`, true, false);
    });

    setSelectionState(selection);

    // didMount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRenderCustomPlaceholder: (
    rowProps: IDetailsRowProps,
    index?: number,
    defaultRender?: (props: IDetailsRowProps) => React.ReactNode
  ) => React.ReactNode = (rowProps, index, defaultRender) => {
    //Make sure we ignore the injected 'null' row or we'll fail to fetch the last page if there's only 1 element remaining.
    if (items.filter((value) => value !== null).length !== totalRows) {
      queueRequestFor?.(index!);
    }

    // custom logic execution
    return defaultRender?.(rowProps);
  };

  const onRenderDetailsHeader = useCallback(
    (detailsHeaderProps) => {
      return isHeaderVisible ? (
        <Sticky stickyPosition={StickyPositionType.Header} isScrollSynced>
          <DetailsHeader
            {...(detailsHeaderProps as IDetailsHeaderBaseProps)}
            styles={{
              root: {
                paddingTop: '0px',
                paddingBottom: '0px',
                height: '30px',
                lineHeight: '30px',
              },
              check: {
                marginTop: '-9px !important',
              },
            }}
          />{' '}
        </Sticky>
      ) : null;
    },
    [isHeaderVisible]
  );

  //Need to delay rendering until selection is set, or saved filters won't be loaded.
  if (!selection.current) return null;

  const sharedProps: IDetailsListProps = {
    items: filteredItems,
    compact: true,
    columns,
    selectionMode: enableRowSelect ? SelectionMode.multiple : SelectionMode.none,
    getKey: (item: any) => item?.id,
    setKey: 'multiple',
    layoutMode: DetailsListLayoutMode.justified,
    isHeaderVisible: true,
    selection: selection.current,
    selectionPreservedOnEmptyClick: true,
    enterModalSelectionOnTouch: true,
    ariaLabelForSelectionColumn: 'Toggle selection',
    ariaLabelForSelectAllCheckbox: 'Toggle selection for all items',
    checkButtonAriaLabel: 'Row checkbox',
    onRenderDetailsHeader: onRenderDetailsHeader,
    onRenderItemColumn,
  };
  const onChange = (event: any) => {
    const fieldValue = event?.target?.value?.toLowerCase?.();
    if (enableLocalSearch && fieldValue) {
      setFilter(fieldValue);
    }
    if (enableRemoteSearch) {
      const fieldName = remoteFilterFieldName || columns[0].fieldName;
      const remoteFilters = remoteFilterGetter({ fieldName: fieldName, fieldValue });

      setRemoteFilters?.(remoteFilters);
    }
  };

  const searchBox =
    enableLocalSearch || enableRemoteSearch ? (
      <TextField underlined={true} placeholder={'Filter...'} onChange={onChange} autoFocus={true} />
    ) : null;

  if (infinite) {
    return (
      <>
        {searchBox}
        <ScrollablePane styles={{ root: { marginTop: '35px' } }}>
          <ShimmeredDetailsList
            {...sharedProps}
            items={infiniteItems}
            onRenderCustomPlaceholder={onRenderCustomPlaceholder as any}
          />
        </ScrollablePane>
      </>
    );
  }
  return (
    <>
      {searchBox}
      <ScrollablePane>
        <MarqueeSelection selection={selection.current}>
          <DetailsList {...sharedProps} />
        </MarqueeSelection>
      </ScrollablePane>
    </>
  );
};

export default FluentList;
